"use client";

import { useEffect, useRef, useState } from "react";
import Container from "@/components/ui/Container";
import SectionHeading from "@/components/ui/SectionHeading";
import Button from "@/components/ui/Button";
import { gsap, ScrollTrigger, useGSAP } from "@/lib/gsap";
import { ChevronLeft, ChevronRight } from "lucide-react";

type Testimonial = {
  quote: string;
  name: string;
  company: string;
  role?: string;
  note?: string;
};

const TESTIMONIALS: readonly Testimonial[] = [
  {
    quote:
      "Instanodes built the infrastructure layer that lets us focus on protocol design instead of node operations. They behave like an extension of our engineering team, which is rare in this space.",
    name: "Shaffy",
    company: "Qubetics",
    role: "CEO",
    note: "Validator network live across three regions",
  },
  {
    quote:
      "Our wallet handles transaction loads we could not have predicted at launch. Instanodes scaled the RPC layer with us, quietly, in the background. Zero user-facing incidents this year.",
    name: "Engineering Lead",
    company: "Cause Coin",
  },
  {
    quote:
      "We needed multi-region failover that meets European compliance and connects cleanly to our card-processing stack. Instanodes delivered both, and stayed engaged through every integration call.",
    name: "CTO",
    company: "EVO Europe",
  },
  {
    quote:
      "The team understood our finance product before we finished explaining it. Their architecture decisions mapped to outcomes that matter: uptime, latency, audit-readiness, not specs on a slide.",
    name: "Head of Infrastructure",
    company: "Enifeno",
  },
];

/* ─── Mobile Slider ──────────────────────────────────────────────────────── */

function MobileSlider({ className = "" }: { className?: string }) {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const slides = Array.from(track.querySelectorAll<HTMLLIElement>("[data-slide]"));
    if (!slides.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        let best: IntersectionObserverEntry | null = null;
        for (const e of entries) {
          if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
        }
        if (best && best.intersectionRatio > 0.6) {
          const idx = Number((best.target as HTMLElement).dataset.slide);
          if (!Number.isNaN(idx)) setActive(idx);
        }
      },
      { root: track, threshold: [0.5, 0.75, 1] },
    );
    slides.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  const goTo = (idx: number) => {
    const track = trackRef.current;
    if (!track) return;
    const slide = track.querySelector<HTMLElement>(`[data-slide="${idx}"]`);
    if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
  };

  return (
    <div className={`relative lg:hidden ${className}`.trim()}>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6"
        style={{ background: "linear-gradient(90deg, var(--canvas) 0%, transparent 100%)" }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6"
        style={{ background: "linear-gradient(-90deg, var(--canvas) 0%, transparent 100%)" }}
      />

      <ul
        ref={trackRef}
        className="no-scrollbar -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-4 pb-2"
        style={{ scrollPaddingInline: "1rem" }}
        aria-label="Client testimonials carousel"
      >
        {TESTIMONIALS.map((t, idx) => {
          const isFeatured = idx === 0;
          return (
            <li
              key={t.company}
              data-slide={idx}
              className="relative flex w-[88%] shrink-0 snap-center flex-col overflow-hidden rounded-2xl border border-border-subtle bg-bg-card p-6"
              style={{
                boxShadow: isFeatured
                  ? "0 18px 48px color-mix(in oklab, var(--accent) 18%, transparent)"
                  : "0 8px 24px color-mix(in oklab, var(--accent) 8%, transparent)",
              }}
            >
              {isFeatured && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 top-0 h-px"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, color-mix(in oklab, var(--accent) 70%, transparent), transparent)",
                  }}
                />
              )}

              <p
                className="mb-3 text-xs font-bold uppercase tracking-widest"
                style={{ color: "var(--accent)" }}
              >
                {t.company}
              </p>

              <span
                aria-hidden
                className="mb-2 block font-serif text-[44px] leading-none opacity-50"
                style={{ color: "var(--accent)" }}
              >
                &ldquo;
              </span>

              <blockquote className="mb-5 flex-1 text-[15px] font-medium leading-relaxed text-text-primary">
                &ldquo;{t.quote}&rdquo;
              </blockquote>

              <footer className="border-t border-border-subtle pt-4">
                <p className="text-sm font-semibold tracking-tight text-text-primary">
                  {t.name}
                </p>
                <p className="mt-0.5 text-xs text-text-secondary">
                  {t.role ? `${t.company}, ${t.role}` : t.company}
                </p>
                {t.note && (
                  <p className="mt-1.5 text-xs text-text-muted">{t.note}</p>
                )}
              </footer>
            </li>
          );
        })}
      </ul>

      <div className="mt-5 flex items-center justify-between gap-4 px-1">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => goTo(Math.max(0, active - 1))}
            disabled={active === 0}
            aria-label="Previous testimonial"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border-subtle bg-bg-card text-text-primary transition-all duration-200 hover:border-border-medium disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft size={16} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => goTo(Math.min(TESTIMONIALS.length - 1, active + 1))}
            disabled={active === TESTIMONIALS.length - 1}
            aria-label="Next testimonial"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border-subtle bg-bg-card text-text-primary transition-all duration-200 hover:border-border-medium disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronRight size={16} aria-hidden />
          </button>
        </div>

        <div className="flex items-center gap-2" role="tablist" aria-label="Select testimonial">
          {TESTIMONIALS.map((t, idx) => {
            const isActive = active === idx;
            return (
              <button
                key={t.company}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={`Go to testimonial ${idx + 1}`}
                onClick={() => goTo(idx)}
                className="h-2 rounded-full transition-all duration-300"
                style={{
                  width: isActive ? 24 : 8,
                  background: isActive
                    ? "var(--accent)"
                    : "color-mix(in oklab, var(--text-primary) 18%, transparent)",
                }}
              />
            );
          })}
        </div>

        <span className="text-xs tabular-nums text-text-muted">
          <span className="font-semibold text-text-primary">{active + 1}</span>
          <span className="opacity-60"> / {TESTIMONIALS.length}</span>
        </span>
      </div>
    </div>
  );
}

/* ─── Main Section ───────────────────────────────────────────────────────── */

export default function ClientSuccess() {
  const sectionRef = useRef<HTMLElement>(null);
  const quoteRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const handleTabClick = (idx: number) => {
    if (idx === active) return;
    const el = quoteRef.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setActive(idx);
      return;
    }
    gsap.to(el, {
      autoAlpha: 0,
      y: -8,
      duration: 0.15,
      ease: "power2.in",
      onComplete: () => {
        setActive(idx);
        requestAnimationFrame(() => {
          gsap.fromTo(
            el,
            { autoAlpha: 0, y: 8 },
            { autoAlpha: 1, y: 0, duration: 0.3, ease: "power2.out" },
          );
        });
      },
    });
  };

  useGSAP(
    () => {
      if (!sectionRef.current) return;
      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        gsap.set(".cs-reveal", { autoAlpha: 0, y: 24 });
        ScrollTrigger.batch(".cs-reveal", {
          start: "top 88%",
          onEnter: (batch) => {
            gsap.to(batch, {
              autoAlpha: 1,
              y: 0,
              duration: 0.55,
              stagger: 0.08,
              ease: "power3.out",
              overwrite: true,
            });
          },
        });
      });

      mm.add("(prefers-reduced-motion: reduce)", () => {
        gsap.set(".cs-reveal", { autoAlpha: 1, y: 0 });
      });
    },
    { scope: sectionRef },
  );

  const current = TESTIMONIALS[active];

  return (
    <section
      ref={sectionRef}
      aria-labelledby="client-success-heading"
      className="section-pad relative overflow-hidden bg-bg-base"
    >
      <div aria-hidden className="bg-glow-dual pointer-events-none absolute inset-0 -z-10 opacity-25" />
      <div aria-hidden className="bg-dot-grid pointer-events-none absolute inset-0 -z-10 opacity-15" />

      <Container>
        <SectionHeading
          title={[
            { text: "The teams running " },
            { text: "production Web3", highlight: true },
            { text: " on Instanodes." },
          ]}
          className="mb-12 sm:mb-14"
        />
        <h2 id="client-success-heading" className="sr-only">
          Client Success
        </h2>

        {/* Mobile slider */}
        <MobileSlider className="cs-reveal" />

        {/* Desktop: tab selector + quote panel */}
        <div className="cs-reveal hidden overflow-hidden rounded-2xl border border-border-subtle lg:grid lg:min-h-[380px] lg:grid-cols-[240px_1fr]">

          {/* Left: company tab list */}
          <ul
            className="flex flex-col border-r border-border-subtle"
            role="tablist"
            aria-label="Select client testimonial"
          >
            {TESTIMONIALS.map((t, idx) => {
              const isActive = active === idx;
              return (
                <li
                  key={t.company}
                  role="presentation"
                  className="relative border-b border-border-subtle last:border-b-0"
                >
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute inset-y-0 left-0 w-[3px]"
                      style={{ background: "var(--accent)" }}
                    />
                  )}
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls="cs-quote-panel"
                    onClick={() => handleTabClick(idx)}
                    className={[
                      "w-full cursor-pointer px-7 py-5 text-left text-base font-semibold tracking-tight transition-colors duration-200",
                      isActive
                        ? "text-text-primary"
                        : "text-text-muted hover:text-text-secondary",
                    ].join(" ")}
                  >
                    {t.company}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Right: quote display */}
          <div
            id="cs-quote-panel"
            className="relative flex flex-col justify-center overflow-hidden px-12 py-10"
          >
            {/* Decorative orb */}
            <span
              aria-hidden
              className="pointer-events-none absolute bottom-0 right-0 h-[440px] w-[440px] rounded-full"
              style={{
                background:
                  "radial-gradient(circle at center, color-mix(in oklab, var(--accent) 7%, transparent) 0%, transparent 70%)",
              }}
            />

            {/* Decorative quote mark — absolute so it doesn't skew vertical centering */}
            <span
              aria-hidden
              className="pointer-events-none absolute left-10 top-8 font-serif text-[72px] leading-none opacity-60"
              style={{ color: "var(--accent)" }}
            >
              &ldquo;
            </span>

            <div ref={quoteRef} className="relative z-10">
              <blockquote className="mb-7 max-w-[600px] text-[20px] font-medium leading-[1.6] text-text-primary">
                &ldquo;{current.quote}&rdquo;
              </blockquote>

              <figcaption>
                <p className="text-base font-semibold tracking-tight text-text-primary">
                  {current.name}
                </p>
                <p className="mt-0.5 text-sm text-text-secondary">
                  {current.role
                    ? `${current.company}, ${current.role}`
                    : current.company}
                </p>
                {current.note && (
                  <p className="mt-2 text-xs text-text-muted">{current.note}</p>
                )}
              </figcaption>
            </div>
          </div>
        </div>

        <div className="cs-reveal mt-10 flex justify-center">
          <Button href="/contact-us" variant="soft" data-cta="talk-to-engineer" data-cta-location="client-success">
            Talk to an Engineer
          </Button>
        </div>
      </Container>
    </section>
  );
}
